import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { zip } from "rxjs";
import type { StateService } from "@uirouter/angularjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faPen, faBriefcase, faHouse, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { Dialog, IconButton } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

import type { ICompaniesRepository } from "App/Modules/Repositories";
import type { ICompany, ISiteSlim } from "App/Modules/Models";
import { SiteState } from "App/Modules/Models";

import "./_company-details.scss";
import { EditCompanyDialog } from "./EditCompanyDialog";

type CompanyDetailsProps = {
    $state: StateService;
    mmCompaniesRepository: ICompaniesRepository;
};

export const CompanyDetails = (props: CompanyDetailsProps) => {
    const { companyId } = props.$state.params;
    const { t } = useTranslation();
    const [company, setCompany] = useState<ICompany>(null);
    const [sites, setSites] = useState<ISiteSlim[]>([]);
    const [showEditCompanyDialog, setShowEditCompanyDialog] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const companyObservable = props.mmCompaniesRepository.getCompany(companyId);
        const sitesObservable = props.mmCompaniesRepository.getSitesForCompany(companyId);

        const sub = zip(companyObservable, sitesObservable).subscribe(
            ([companyResponse, sitesResponse]) => {
                setCompany(companyResponse.data);
                setSites(sitesResponse.data);
                setLoading(false);
            },
            (error) => {
                setCompany(error);
                setLoading(false);
            },
        );

        return () => {
            sub.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const findStateModifier = (site) => {
        if (site.siteState === "Normal") {
            return "state-tag--approve";
        }
        if (site.siteState === "Maintenance") {
            return "state-tag--info";
        }
        if (site.siteState === "Not started") {
            return "state-tag--warning";
        }
        return "";
    };

    return (
        <div className="company-details">
            <div className="company-details__header">
                <h1 className="title title--h1">
                    {loading ? <Skeleton variant="text" animation="wave" height="1.6em" width="25ch" /> : company?.name}
                </h1>
            </div>

            <div className="company-details__section-wrapper">
                <div className="card company-details__section">
                    {loading ? (
                        <Skeleton variant="rect" animation="wave" height="150px" width="100%" />
                    ) : (
                        <>
                            <div className="company-details__header-with-edit">
                                <h3 className="title title--h3 title--no-spacing">
                                    <FontAwesomeIcon icon={faBriefcase} />
                                    {t("Gui.Common.Details")}
                                </h3>
                                <IconButton
                                    className="icon-button"
                                    onClick={() => {
                                        setShowEditCompanyDialog(true);
                                    }}
                                >
                                    <FontAwesomeIcon className="icon-button__icon" icon={faPen} />
                                </IconButton>
                            </div>

                            <div className="data-panels data-panels--space-between">
                                <div className="data-panels__panel">
                                    <p className="caption">{t("Gui.Common.Name")}</p>
                                    <p className="paragraph paragraph--bold">{company?.name}</p>
                                </div>
                                <div className="data-panels__panel data-panels__panel--right-align">
                                    <div className="data-panels__heading">
                                        <p className="caption">{t("Entities.Common.CorporateIdNumber")}</p>
                                    </div>
                                    <div className="data-panels__value">
                                        <p className="paragraph paragraph--bold">
                                            {company?.corporateIdNumber || t("Entities.Common.NoCorporateIdNumber")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="card company-details__section--large">
                    {loading ? (
                        <Skeleton variant="rect" animation="wave" height="150px" width="100%" />
                    ) : (
                        <div className="scrollable-list">
                            <h3 className="title title--h3 scrollable-list__header">
                                <FontAwesomeIcon fixedWidth icon={faBuilding} /> {t("Entities.Site.Sites")}
                                <span className="caption scrollable-list__caption">
                                    {`– ${sites.length} ${t("Entities.Value.Found")}`}
                                </span>
                            </h3>

                            <div className="scrollable-list__content scrollable-list__content--dark">
                                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                                    {sites.length === 0 ? (
                                        <p className="caption caption--italic">
                                            {t("Gui.Common.Companies.NoSitesFound")}
                                        </p>
                                    ) : (
                                        <ul className="entity-list">
                                            {sites.map((site) => (
                                                <li className="entity-list__item" key={site.id}>
                                                    <a href={`/#!/sites/${site.id}`} className="entity-list__item-link">
                                                        <div className="entity-list__item-icon">
                                                            <FontAwesomeIcon icon={faHouse} />
                                                        </div>
                                                        <div className="entity-list__item-panel">
                                                            <div className="entity-list__item-value">
                                                                <p className="paragraph paragraph--bold">
                                                                    {site.name}&nbsp;
                                                                    <span className="caption">
                                                                        - {site.partner.name}
                                                                    </span>
                                                                </p>
                                                                <p className={`state-tag ${findStateModifier(site)}`}>
                                                                    {t(
                                                                        `Enums.SiteState.Enum${
                                                                            SiteState[site.siteState]
                                                                        }`,
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="entity-list__item-arrow">
                                                            <FontAwesomeIcon icon={faChevronRight} />
                                                        </div>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={showEditCompanyDialog} fullWidth>
                <EditCompanyDialog
                    company={company}
                    onClose={() => setShowEditCompanyDialog(false)}
                    mmCompaniesRepository={props.mmCompaniesRepository}
                    onDeleteSuccess={() => {
                        props.$state.go("companies");
                    }}
                    onEditSuccess={(updatedCompany) => {
                        setCompany(updatedCompany);
                    }}
                />
            </Dialog>
        </div>
    );
};
